import React, { useState } from 'react';

import { Alert, Anchor, ButtonMantine, Card, Center, Container, Stack, Text } from 'ui';
import { CollapsibleCard } from 'ui/Core';
import { isNotEmpty } from 'ui/Form';
import { GreenCircleCheck, IconBling } from 'ui/Icons';

import { ICredential, useTokenizeStripeCard } from '../../../api';
import { TokenEx } from '../../../hooks/TokenEx';
import { PagesUrl } from '../../../utils';
import { TokenizedCredentialCard } from '../TokenizedCard';
import classes from './LinkYourCard.module.css';

type LinkYourCardProps = {
  isCardExpanded?: boolean;
  credential?: ICredential;
  onComplete?: () => void;
  onSkip?: () => void;
  children?: React.ReactElement | ((onComplete: () => void) => React.ReactElement);
};

interface LinkYourCardPropsWithChildren extends LinkYourCardProps {
  credential?: never;
  children(onComplete: () => void): React.ReactElement;
}

type TokenizedCardForm = {
  credential?: ICredential;
  onComplete?: () => void;
  onSkip?: () => void;
  setIsLinkSuccess: (arg: boolean) => void;
};

const AgreeToTerms = () => {
  return (
    <Card bg="gray.0" p="sm">
      <Stack gap="xs">
        <Text fz="sm" fw={600}>
          Agree to{' '}
          <Anchor
            href={`https://www.givebacks.com${PagesUrl.TERMS_of_SERVICE}`}
            target="_blank"
            c="cobalt"
            inherit
            fw={600}
          >
            Terms & Conditions
          </Anchor>
        </Text>
        <Text fz={10}>
          By registering an eligible card, you authorize your payment card network (i.e.,
          Mastercard, Visa, American Express) to monitor and share transaction data with the Rakuten
          Card Linked Offer Network, Inc. (RCLON), Rewards Network & Fidel API to qualify your
          transactions to earn rewards at participating local restaurants and retailers and provide
          you with offers that may be of interest to you. You also acknowledge and agree that RCLON,
          Rewards Network and Fidel API may share details of your qualifying transactions with
          Givebacks and participating local restaurants and retailers to support the program in
          accordance with the Givebacks Eligible Card Linked Offer{' '}
          <Anchor
            href={`https://www.givebacks.com${PagesUrl.TERMS_of_SERVICE}`}
            target="_blank"
            c="cobalt"
            inherit
          >
            Terms and Conditions
          </Anchor>{' '}
          and{' '}
          <Anchor
            href={`https://www.givebacks.com${PagesUrl.TERMS_of_SERVICE}`}
            target="_blank"
            c="cobalt"
            inherit
          >
            Privacy Policy
          </Anchor>{' '}
          You can un-enroll an eligible card from the Givebacks program at any time by clicking
          Add/Edit eligible card under your Profile and selecting the eligible card you wish to
          remove. Most Visa, Mastercard and American Express payment cards are eligible for the
          program. When using an eligible debit card, please perform a signature debit transaction.
          Do not enter your PIN when using your eligible debit card to make a purchase if you want
          to earn cash back for your transaction.
        </Text>
      </Stack>
    </Card>
  );
};

const ExistingCredentialForm = ({
  credential,
  onComplete,
  onSkip,
  setIsLinkSuccess,
}: TokenizedCardForm) => {
  const form = TokenEx.useTokenExForm({
    initialValues: {
      terms: false,
      name: '',
      month: '',
      year: '',
      zip: '',
    },
    validate: {
      terms: isNotEmpty(),
    },
  });

  const { mutate: tokenizeCard } = useTokenizeStripeCard();
  const [error, setErrors] = useState<undefined | string>();

  const handleTokenizeCard = () => {
    if (credential?.uuid) {
      tokenizeCard(credential?.uuid, {
        onSuccess: () => {
          setIsLinkSuccess(true);
          onComplete?.();
        },
        onError: (e: Error) => {
          const error =  e as { response?: { data?: { error?: string } } };
          setErrors(error?.response?.data?.error ?? 'Unknown error occurred, please try again.');
        },
      });
    }
  };

  return (
    <Stack>
      <TokenEx.Provider form={form}>
        <TokenizedCredentialCard credential={credential} showFooter={true} />
        {/* <TokenEx.AgreeToTerms /> */}
        <AgreeToTerms />
        <Alert.Transition title={error} type="error" mounted={Boolean(error)} />
        <Stack className={classes.AgreeToTerms}>
          {/* <TokenEx.SubmitButton
            onClick={handleTokenizeCard}
            variant='gradient'
            label="Agree to Terms & Conditions"
          /> */}
          <ButtonMantine onClick={handleTokenizeCard} variant="gradient" fullWidth>
            Agree to Terms & Conditions
          </ButtonMantine>
          <Center>
            <ButtonMantine
              variant="subtle"
              c="gray.8"
              onClick={() => {
                onSkip?.();
              }}
            >
              Skip for now
            </ButtonMantine>
          </Center>
        </Stack>
      </TokenEx.Provider>
    </Stack>
  );
};

export const LinkYourCard = ({
  isCardExpanded = false,
  credential,
  onComplete,
  onSkip,
  children,
}: LinkYourCardProps | LinkYourCardPropsWithChildren) => {
  const [isLinkSuccess, setIsLinkSuccess] = useState(false);

  return (
    <CollapsibleCard
      headerTitle="Link Your Card to Givebacks"
      isCardExpanded={isCardExpanded}
      headerContent={
        isLinkSuccess && (
          <Alert type="info-blue" icon={<IconBling />}>
            Note: Make sure to run any debit transactions/cards as credit to receive cash back for
            this offer.
          </Alert>
        )
      }
      subtitle={
        isLinkSuccess
          ? 'Great! You can use this card for in store and online cash back offers! Earn money for you and your cause!'
          : 'To support us for free through your everyday purchases, you must link your card and agree to our terms and conditions.'
      }
      endAdornment={
        isLinkSuccess && <GreenCircleCheck className="w-[22px] h-[22px] text-[#20C0AD]" />
      }
    >
      <Container px={0} mt={{ base: 'md', sm: 'lg' }}>
        {!!credential ? (
          <ExistingCredentialForm
            credential={credential}
            onComplete={onComplete}
            setIsLinkSuccess={setIsLinkSuccess}
            onSkip={onSkip}
          />
        ) : typeof children == 'function' ? (
          children?.(() => setIsLinkSuccess(true))
        ) : (
          children
        )}
      </Container>
    </CollapsibleCard>
  );
};
